import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { BookOpen, Briefcase, ChevronRight, Coins, Compass, Hammer, LifeBuoy, ListChecks, MessageSquare, MoreHorizontal, PanelLeft, Share2, Rocket, UploadCloud, Users, LayoutGrid } from 'lucide-react'
import { AppHeader } from '#/components/layout/app-header'
import { Button } from '#/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '#/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetTitle } from '#/components/ui/sheet'
import { RoadmapCanvas } from '#/components/roadmap/roadmap-canvas'
import { PrdPanel } from '#/components/roadmap/prd-panel'
import { FeaturePanel } from '#/components/roadmap/feature-panel'
import { TaskBoard } from '#/components/tasks/task-board'
import { WikiView } from '#/components/workspace/wiki-view'
import { ImplementDialog } from '#/components/workspace/implement-dialog'
import { ShareDialog } from '#/components/workspace/share-dialog'
import { FeedbackBanner } from '#/components/workspace/feedback-banner'
import { PlanChat } from '#/components/workspace/plan-chat'
import { Markdown } from '#/components/common/markdown'
import { useIsDesktop } from '#/components/common/use-media-query'
import { ResizablePane } from '#/components/common/resizable-pane'
import { WorkspaceSimpleTabs } from '#/components/layout/workspace-simple-tabs'
import { useFeatures, usePlan, useSubFeatures, useTaskStream, useTasks, useWorkspacePlans } from '#/lib/api'
import { useBuilderMode } from '#/lib/simple-mode'
import { useMarkSeen } from '#/lib/api-inbox'
import { PrimaryAction } from '#/components/common/primary-action'
import { m } from '#/paraglide/messages'

const VIEWS = ['roadmap', 'task', 'wiki', 'prd'] as const
type View = (typeof VIEWS)[number]

export const Route = createFileRoute('/workspace/$workspaceId/$view')({
  validateSearch: z.object({ plan: z.string().optional(), feature: z.string().optional() }),
  component: WorkspaceView,
})

function WorkspaceView() {
  const { workspaceId, view: rawView } = Route.useParams()
  const view = (VIEWS as readonly string[]).includes(rawView) ? (rawView as View) : 'roadmap'
  const { plan: planParam, feature: featureParam } = Route.useSearch()
  const navigate = useNavigate()
  const desktop = useIsDesktop()
  // Non-tech F0: the header swaps Wiki/Task/Biaya/Tim/Studio for the four plain-language tabs
  // when the signed-in user is in simple mode (docs/nontech/CONTRACT.md). Dev mode is untouched.
  const { simple } = useBuilderMode()
  const { data: ws } = useWorkspacePlans(workspaceId)
  // resolve the plan: ?plan= or the latest completed plan in the workspace
  const planId = planParam ?? [...(ws?.plans ?? [])].reverse().find((p) => p.status === 'completed')?.id ?? ws?.plans.at(-1)?.id
  useEffect(() => {
    if (!planParam && planId) navigate({ to: '/workspace/$workspaceId/$view', params: { workspaceId, view }, search: { plan: planId }, replace: true })
  }, [planParam, planId, workspaceId, view, navigate])
  // Inspirasi F1: opening the workspace counts everything done so far as seen
  const markSeen = useMarkSeen()
  useEffect(() => {
    markSeen.mutate(workspaceId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId])
  const { data: full } = usePlan(planId ?? '', { refetchInterval: false })
  const { data: fs } = useFeatures(planId ?? '', Boolean(planId))
  const { data: sfs } = useSubFeatures(planId ?? '', Boolean(planId))
  // live task updates over SSE; polling (15 s) only while the stream is down
  const live = useTaskStream(planId ?? '', Boolean(planId))
  const { data: ts } = useTasks(planId ?? '', { enabled: Boolean(planId), refetchInterval: live ? false : 15000 })
  const features = fs?.features ?? []
  const subFeatures = sfs?.subFeatures ?? []
  const tasks = ts?.tasks ?? []
  const plan = full?.plan
  const documents = full?.documents ?? []
  const prd = documents[0]?.content ?? ''
  const [selected, setSelected] = useState<string | null>(featureParam ?? null)
  const [subsFor, setSubsFor] = useState<string | null>(null)
  const [leftOpen, setLeftOpen] = useState(true)
  const [agentOpen, setAgentOpen] = useState(true)
  const [leftSheet, setLeftSheet] = useState(false)
  const [agentSheet, setAgentSheet] = useState(false)
  const [implOpen, setImplOpen] = useState(false)
  const [shareOpen, setShareOpen] = useState(false)
  const selIdx = useMemo(() => features.findIndex((f) => f.id === selected), [features, selected])
  const selFeature = selIdx >= 0 ? features[selIdx] : null
  const scope = view === 'wiki' ? m.agent_scope_wiki() : view === 'task' ? (selFeature ? m.agent_scope_task_feature() : m.agent_scope_workspace()) : selFeature ? m.agent_scope_roadmap_feature() : m.agent_scope_roadmap()
  const select = (id: string | null) => {
    setSelected(id)
    if (id !== subsFor) setSubsFor(null)
    if (id && !desktop) setLeftSheet(true)
  }
  const showSubs = (fid: string) => {
    setSelected(fid)
    setSubsFor(fid)
    if (!desktop) setLeftSheet(true)
  }
  const goTasks = () => navigate({ to: '/workspace/$workspaceId/$view', params: { workspaceId, view: 'task' }, search: { plan: planId } })

  const NavBtn = ({ v, icon, label }: { v: View; icon: React.ReactNode; label: string }) => (
    <Button asChild size="sm" variant={view === v ? 'secondary' : 'ghost'} className="h-8 gap-1.5 text-xs">
      <Link to="/workspace/$workspaceId/$view" params={{ workspaceId, view: v }} search={{ plan: planId }} aria-label={label} title={label}>{icon} <span className="hidden xl:inline">{label}</span></Link>
    </Button>
  )
  // sibling pages (own route files, not a $view): cost (moat §4.1) and the client studio (Phase 3)
  const CostBtn = () => (
    <Button asChild size="sm" variant="ghost" className="h-8 gap-1.5 text-xs">
      <Link to="/workspace/$workspaceId/cost" params={{ workspaceId }} search={{ plan: planId }} aria-label={m.cost_nav()} title={m.cost_nav()}><Coins className="size-4" /> <span className="hidden xl:inline">{m.cost_nav()}</span></Link>
    </Button>
  )
  const TeamBtn = () => (
    <Button asChild size="sm" variant="ghost" className="h-8 gap-1.5 text-xs">
      <Link to="/workspace/$workspaceId/settings" params={{ workspaceId }} aria-label={m.tm_nav()} title={m.tm_nav()}><Users className="size-4" /> <span className="hidden xl:inline">{m.tm_nav()}</span></Link>
    </Button>
  )
  const StudioBtn = () => (
    <Button asChild size="sm" variant="ghost" className="h-8 gap-1.5 text-xs">
      <Link to="/workspace/$workspaceId/studio" params={{ workspaceId }} aria-label={m.cost_nav_studio()} title={m.cost_nav_studio()}><Briefcase className="size-4" /> <span className="hidden xl:inline">{m.cost_nav_studio()}</span></Link>
    </Button>
  )

  const leftPanel = (onClose: () => void) =>
    selFeature && view === 'roadmap' ? (
      <FeaturePanel
        feature={selFeature}
        index={selIdx}
        total={features.length}
        tasks={tasks.filter((t) => t.featureId === selFeature.id)}
        subFeatures={subFeatures}
        workspaceId={workspaceId}
        planId={planId ?? ''}
        showSubs={subsFor === selFeature.id}
        onShowSubs={(s) => setSubsFor(s ? selFeature.id : null)}
        onPrev={() => select(features[(selIdx - 1 + features.length) % features.length]?.id ?? null)}
        onNext={() => select(features[(selIdx + 1) % features.length]?.id ?? null)}
        onClose={() => { setSelected(null); setSubsFor(null); if (!desktop) onClose() }}
        onDeleted={() => select(features.filter((f) => f.id !== selFeature.id)[Math.min(selIdx, features.length - 2)]?.id ?? null)}
      />
    ) : (
      <PrdPanel planId={planId ?? ''} documents={documents} featureCount={features.length} onClose={onClose} />
    )

  const chat = (onClose: () => void) =>
    planId ? (
      <PlanChat planId={planId} scope={scope} selectedFeatureId={selected} counts={{ features: features.length, subfeatures: subFeatures.length, tasks: tasks.length }} initialMessages={full?.chatMessages ?? []} onClose={onClose} />
    ) : null

  const hasLeft = view === 'roadmap' || view === 'prd'

  return (
    <>
      <AppHeader
        showStats={false}
        compact
        left={
          <nav className="hidden items-center gap-1 truncate text-sm text-muted-foreground lg:flex">
            <span>/</span>
            <Link to="/workspace/$workspaceId" params={{ workspaceId }} className="truncate hover:text-foreground">{ws?.workspace.name ?? '…'}</Link>
            <span>/</span>
            <span className="truncate text-foreground">{plan ? m.ws_breadcrumb_plan({ n: plan.cycle }) : '…'}</span>
          </nav>
        }
        right={
          <div className="flex items-center gap-1">
            {/* desktop actions */}
            <div className="hidden items-center gap-1 xl:flex">
              <Button size="icon" variant="ghost" className="size-8" aria-label={m.roadmap_share()} title={m.roadmap_share()} onClick={() => setShareOpen(true)} disabled={!planId}><Share2 className="size-4" /></Button>
              {/* Non-tech F0: simple mode swaps Wiki/Task/Biaya/Tim/Studio for the four plain tabs. Dev mode below is untouched. */}
              {simple ? (
                <WorkspaceSimpleTabs workspaceId={workspaceId} planId={planId} active="roadmap" />
              ) : (
                <>
                  <NavBtn v="wiki" icon={<BookOpen className="size-4" />} label={m.roadmap_wiki()} />
                  <NavBtn v="task" icon={<ListChecks className="size-4" />} label={m.roadmap_view_tasks()} />
                  <CostBtn />
                  <TeamBtn />
                  <StudioBtn />
                  {view === 'roadmap' ? (
                    <Button size="sm" className="h-8 gap-1" onClick={goTasks} disabled={!planId}>{m.roadmap_continue()} <ChevronRight className="size-4" /></Button>
                  ) : null}
                  {/* Inspirasi F2: compact state-following action, tooltip = execution mode in human words */}
                  <PrimaryAction planId={planId} mode="dev" size="sm" execMode={plan?.execution?.mode ?? null} />
                </>
              )}
              <Button size="sm" variant={view === 'roadmap' ? 'outline' : 'default'} className="h-8 gap-1.5" onClick={() => setImplOpen(true)} disabled={!planId}><Rocket className="size-4" /> {m.roadmap_implement()}</Button>
              <Button size="icon" variant={agentOpen ? 'secondary' : 'ghost'} className="size-8" onClick={() => setAgentOpen((o) => !o)} aria-label={m.agent_title()} aria-pressed={agentOpen}><MessageSquare className="size-4" /></Button>
            </div>
            {/* mobile / tablet actions: panels become sheets, the rest folds into a menu */}
            <div className="flex items-center gap-1 xl:hidden">
              {hasLeft ? (
                <Button size="sm" variant="ghost" className="h-8 gap-1.5 px-2 text-xs" onClick={() => setLeftSheet(true)} aria-label={m.ws_mobile_plan()}><PanelLeft className="size-4" /> <span className="hidden sm:inline">{m.ws_mobile_plan()}</span></Button>
              ) : null}
              <Button size="sm" variant="ghost" className="h-8 gap-1.5 px-2 text-xs" onClick={() => setAgentSheet(true)} disabled={!planId} aria-label={m.ws_mobile_agent()}><MessageSquare className="size-4" /> <span className="hidden sm:inline">{m.ws_mobile_agent()}</span></Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon" variant="ghost" className="size-8" aria-label={m.common_more()}><MoreHorizontal className="size-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {/* Non-tech F0: simple mode swaps Wiki/Task/Biaya/Tim/Studio/Room for the four plain tabs. */}
                  {simple ? (
                    <>
                      <DropdownMenuItem asChild><Link to="/workspace/$workspaceId/$view" params={{ workspaceId, view: 'roadmap' }} search={{ plan: planId }}><Compass className="size-4" /> {m.prd_panel_title()}</Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link to="/workspace/$workspaceId/bangun" params={{ workspaceId }} search={{ plan: planId }}><Hammer className="size-4" /> {m.simple_nav_bangun()}</Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link to="/workspace/$workspaceId/terbitkan" params={{ workspaceId }}><UploadCloud className="size-4" /> {m.pb_nav()}</Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link to="/workspace/$workspaceId/bantuan" params={{ workspaceId }}><LifeBuoy className="size-4" /> {m.simple_nav_bantuan()}</Link></DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem asChild><Link to="/workspace/$workspaceId/$view" params={{ workspaceId, view: 'roadmap' }} search={{ plan: planId }}><ChevronRight className="size-4" /> {m.prd_panel_title()}</Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link to="/workspace/$workspaceId/$view" params={{ workspaceId, view: 'task' }} search={{ plan: planId }}><ListChecks className="size-4" /> {m.roadmap_view_tasks()}</Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link to="/workspace/$workspaceId/$view" params={{ workspaceId, view: 'wiki' }} search={{ plan: planId }}><BookOpen className="size-4" /> {m.roadmap_wiki()}</Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link to="/workspace/$workspaceId/cost" params={{ workspaceId }} search={{ plan: planId }}><Coins className="size-4" /> {m.cost_nav()}</Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link to="/workspace/$workspaceId/room" params={{ workspaceId }}><LayoutGrid className="size-4" /> {m.rm_nav()}</Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link to="/workspace/$workspaceId/settings" params={{ workspaceId }}><Users className="size-4" /> {m.tm_nav()}</Link></DropdownMenuItem>
                      <DropdownMenuItem asChild><Link to="/workspace/$workspaceId/studio" params={{ workspaceId }}><Briefcase className="size-4" /> {m.cost_nav_studio()}</Link></DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShareOpen(true)} disabled={!planId}><Share2 className="size-4" /> {m.roadmap_share()}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setImplOpen(true)} disabled={!planId}><Rocket className="size-4" /> {m.roadmap_implement()}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        }
      />
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <FeedbackBanner />
        <div className="flex min-h-0 min-w-0 flex-1">
          {hasLeft ? (
            <>
              {leftOpen && desktop ? (
                <ResizablePane side="left" storageKey={view === 'prd' ? 'bystep.pane.prd' : 'bystep.pane.left'} initial={view === 'prd' ? 560 : 440} min={300} max={960} className="hidden min-w-0 lg:block">
                  {leftPanel(() => setLeftOpen(false))}
                </ResizablePane>
              ) : null}
              <div className="relative min-w-0 flex-1">
                {view === 'prd' ? (
                  <div className="h-full overflow-y-auto p-4 sm:p-6"><Markdown content={prd} className="mx-auto max-w-3xl" /></div>
                ) : (
                  <RoadmapCanvas planId={planId ?? ''} workspaceId={workspaceId} title={plan?.title ?? ''} features={features} subFeatures={subFeatures} tasks={tasks} selectedId={selected} onSelect={select} onShowSubs={showSubs} onDeleted={(id) => select(features.filter((f) => f.id !== id)[Math.min(features.findIndex((f) => f.id === id), features.length - 2)]?.id ?? null)} />
                )}
                {!leftOpen && desktop ? <button className="absolute left-3 top-3 z-10 rounded-lg border bg-card px-2 py-1 text-xs shadow-sm hover:border-primary/60" onClick={() => setLeftOpen(true)}>{m.prd_panel_title()}</button> : null}
                {view === 'roadmap' && fs && !features.length ? (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-6">
                    <div className="pointer-events-auto max-w-xs rounded-2xl border bg-card p-5 text-center shadow-sm">
                      <div className="font-heading font-semibold">{m.feat_empty_title()}</div>
                      <p className="mt-1 text-sm text-muted-foreground">{m.feat_empty_desc()}</p>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          ) : view === 'task' ? (
            <div className="min-w-0 flex-1">{planId ? <TaskBoard planId={planId} features={features} tasks={tasks} initialFeature={featureParam} /> : null}</div>
          ) : (
            <div className="min-w-0 flex-1 overflow-y-auto"><WikiView workspaceId={workspaceId} planId={planId} /></div>
          )}
          {agentOpen && planId && desktop ? (
            <ResizablePane side="right" storageKey="bystep.pane.agent" initial={410} min={300} max={800} className="hidden min-w-0 lg:block">
              {chat(() => setAgentOpen(false))}
            </ResizablePane>
          ) : null}
        </div>
      </main>

      {/* ≤1024px: panels as sheets */}
      {!desktop ? (
        <>
          <Sheet open={leftSheet} onOpenChange={setLeftSheet}>
            <SheetContent side="left" className="w-full gap-0 p-0 sm:max-w-md" showCloseButton={false}>
              <SheetTitle className="sr-only">{m.prd_panel_title()}</SheetTitle>
              {leftPanel(() => setLeftSheet(false))}
            </SheetContent>
          </Sheet>
          <Sheet open={agentSheet} onOpenChange={setAgentSheet}>
            <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-md" showCloseButton={false}>
              <SheetTitle className="sr-only">{m.agent_title()}</SheetTitle>
              {chat(() => setAgentSheet(false))}
            </SheetContent>
          </Sheet>
        </>
      ) : null}
      {planId ? <ImplementDialog open={implOpen} onOpenChange={setImplOpen} planId={planId} workspaceId={workspaceId} /> : null}
      {planId ? <ShareDialog planId={planId} open={shareOpen} onOpenChange={setShareOpen} /> : null}
    </>
  )
}
